import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSocket } from 'src/context/SocketContext';

const columns = [
  // { id: 'userId', label: 'User ID', align: 'center' },
  { id: 'createdAt', label: 'Created At', align: 'center' },
  { id: 'label', label: 'Label', align: 'center' },
  { id: 'email', label: 'Email', align: 'center' },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date)
    ? '-'
    : date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
};
const ActivityTable = ({ email }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [clearFilter, setClearFilter] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const socket = useSocket();

  const fetchActivityData = async (page = 0, limit = rowsPerPage, startDate = null, endDate = null) => {
    setLoading(true);
    try {
      const payload = {
        email,
        skip: page * limit,
        limit,
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
      };

      socket.emit('getUserActivity', payload, ({ data: { userActivity, userActivityCount } }) => {
        const activityArray = userActivity?.result || [];
        const total = userActivityCount || 0;

        const formattedRows = activityArray?.map((item) => ({
          userId: item.userId || '-',
          createdAt: item.createdAt || '-',
          label: item.label || '-',
          email: item.email || '-',
        }));

        setRows(formattedRows);
        setTotalCount(total);
      });
    } catch (error) {
      console.error('Error fetching activity data:', error);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (!dateRange.startDate && !dateRange.endDate) return;
    setPage(0);
    setClearFilter(true);
    fetchActivityData(0, rowsPerPage, dateRange.startDate, dateRange.endDate);
  };

  const handleClearFilter = () => {
    setDateRange({ startDate: null, endDate: null });
    setPage(0);
    setClearFilter(false);
    fetchActivityData(0, rowsPerPage);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchActivityData(newPage, rowsPerPage, dateRange.startDate, dateRange.endDate);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchActivityData(0, newLimit, dateRange.startDate, dateRange.endDate);
  };

  useEffect(() => {
    fetchActivityData();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            mb: 2,
            mx: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => setDateRange((prev) => ({ ...prev, startDate: newValue }))}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 140, '.MuiInputBase-input': { p: 1 }, '& label': { zIndex: 0 } },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => setDateRange((prev) => ({ ...prev, endDate: newValue }))}
              minDate={dateRange.startDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 140, '.MuiInputBase-input': { p: 1 }, '& label': { zIndex: 0 } },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyFilter}
              disabled={!dateRange.startDate && !dateRange.endDate}
              sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
            >
              Apply Filter
            </Button>
            {clearFilter && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilter}
                sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
              >
                Clear Filter
              </Button>
            )}
          </Box>
          <Box sx={{ fontWeight: 'bold', mt: { xs: 2, sm: 0 } }}>Total: {totalCount}</Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ flex: 1, maxHeight: 'calc(100vh - 300px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns?.map((col) => (
                      <TableCell key={col.id} align={col.align} sx={{ fontWeight: 'bold', padding: '8px' }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{
                          py: 6,
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: '#6c757d',
                          fontStyle: 'italic',
                          backgroundColor: '#f0f4f8',
                          letterSpacing: 1,
                        }}
                      >
                        🚫 No activity data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows?.map((row, idx) => (
                      <TableRow key={idx}>
                        {columns?.map((col) => (
                          <TableCell key={col.id} align={col.align}>
                            {col.id === 'createdAt' ? formatDate(row[col.id]) : row[col.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ActivityTable;
